import React, { useState, useEffect, useMemo } from 'react';
import { 
  BarChart, 
  Calendar, 
  Filter, 
  Search, 
  TrendingUp, 
  DollarSign, 
  CheckCircle, 
  Clock, 
  AlertTriangle,
  X,
  FileText,
  Eye,
  Scissors,
  Package
} from 'lucide-react';
import Card, { CardHeader, CardContent } from '../components/ui/Card';
import Button from '../components/ui/Button';
import Select from '../components/ui/Select';
import Input from '../components/ui/Input';
import { useAppStore } from '../store';
import { supabase } from '../lib/supabase';
import { formatCurrency } from '../utils/formatters';
import { generateProductionAdminPDF } from '../utils/pdfGenerator';
import { Bar, Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
} from 'chart.js';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

// Define filter types
interface Filters {
  clientId: string;
  productionId: string;
  taskType: string;
  supplierId: string;
  taskStatus: string;
  dateRange: string;
  startDate: string;
  endDate: string;
  period: string;
  customDateRange: boolean;
}

// Define KPI types
interface KPIs {
  averageProfitability: number;
  totalCost: number;
  totalValue: number;
  pendingTasksCount: number;
  inProgressTasksCount: number;
  finishedTasksCount: number;
}

const ProductionAdmin: React.FC = () => {
  const { productions, clients, suppliers, productionTasks, samples, sampleTasks, fetchSamples, fetchSampleTasks } = useAppStore();
  // Cambiar el valor inicial de filters para que el período por defecto sea el mes corriente
  const [filters, setFilters] = useState<Filters>({
    clientId: 'all',
    productionId: 'all',
    taskType: 'all',
    supplierId: 'all',
    taskStatus: 'all',
    dateRange: 'created_at',
    startDate: '',
    endDate: '',
    period: 'month', // por defecto mes corriente
    customDateRange: false,
  });
  const [taskTypes, setTaskTypes] = useState<{id: string, name: string}[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [kpis, setKpis] = useState<KPIs>({
    averageProfitability: 0,
    totalCost: 0,
    totalValue: 0,
    pendingTasksCount: 0,
    inProgressTasksCount: 0,
    finishedTasksCount: 0,
  });
  const [taskTypeChartData, setTaskTypeChartData] = useState<any>(null);
  const [supplierChartData, setSupplierChartData] = useState<any>(null);
  const [viewType, setViewType] = useState<'productions' | 'samples'>('productions');
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const isDarkMode = document.documentElement.classList.contains('dark');

  // Get productions for the selected client
  const clientProductions = useMemo(() => {
    if (filters.clientId === 'all') return [];
    return productions.filter(p => p.client_id === filters.clientId);
  }, [productions, filters.clientId]);

  // Fetch task types and samples
  useEffect(() => {
    const fetchTaskTypes = async () => {
      try {
        const { data, error } = await supabase
          .from('task_types')
          .select('id, name')
          .order('name');
        
        if (error) throw error;
        setTaskTypes(data || []);
      } catch (error) {
        console.error('Error fetching task types:', error);
      }
    };

    const loadData = async () => {
      await fetchTaskTypes();
      await fetchSamples();
      await fetchSampleTasks();
    };

    loadData();
  }, [fetchSamples, fetchSampleTasks]);

  // Apply date filters based on period or custom date range
  useEffect(() => {
    if (filters.customDateRange) {
      // If custom date range is selected, don't modify the dates
      return;
    }
    
    if (filters.period === 'all') {
      setFilters(prev => ({ ...prev, startDate: '', endDate: '' }));
      return;
    }

    const now = new Date();
    let startDate = new Date();
    
    switch (filters.period) {
      case '7days':
        startDate.setDate(now.getDate() - 7);
        break;
      case '30days':
        startDate.setDate(now.getDate() - 30);
        break;
      case 'month':
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        break;
      case 'lastMonth':
        startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const endDate = new Date(now.getFullYear(), now.getMonth(), 0);
        setFilters(prev => ({
          ...prev,
          startDate: startDate.toISOString().split('T')[0],
          endDate: endDate.toISOString().split('T')[0],
        }));
        return;
      case 'quarter':
        startDate = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1);
        break;
      case 'year':
        startDate = new Date(now.getFullYear(), 0, 1);
        break;
    }

    setFilters(prev => ({
      ...prev,
      startDate: startDate.toISOString().split('T')[0],
      endDate: now.toISOString().split('T')[0],
    }));
  }, [filters.period, filters.customDateRange]);

  // Reset productionId when clientId changes
  useEffect(() => {
    setFilters(prev => ({
      ...prev,
      productionId: 'all'
    }));
  }, [filters.clientId]);

  // Filter productions/samples based on criteria
  const filteredProductions = useMemo(() => {
    const dataSource = viewType === 'productions' ? productions : samples;
    
    return dataSource.filter(item => {
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const itemName = item.name?.toLowerCase() || '';
        const clientName = clients.find(c => c.id === item.client_id)?.name?.toLowerCase() || '';
        
        if (!itemName.includes(query) && !clientName.includes(query)) {
          return false;
        }
      }

      // Filter by client
      if (filters.clientId !== 'all' && item.client_id !== filters.clientId) {
        return false;
      }

      // Filter by production ID
      if (filters.productionId !== 'all' && item.id !== filters.productionId) {
        return false;
      }

      // Filter by date range
      if (filters.startDate && filters.endDate) {
        const itemDate = new Date(
          filters.dateRange === 'created_at' 
            ? item.created_at 
            : item.delivery_date
        );
        const startDate = new Date(filters.startDate);
        const endDate = new Date(filters.endDate);
        endDate.setHours(23, 59, 59); // Include the entire end day

        if (itemDate < startDate || itemDate > endDate) {
          return false;
        }
      }

      return true;
    });
  }, [productions, samples, viewType, searchQuery, clients, filters]);

  // Get production IDs for task filtering
  const filteredProductionIds = useMemo(() => {
    return filteredProductions.map(p => p.id);
  }, [filteredProductions]);

  // Filter tasks based on criteria
  const filteredTasks = useMemo(() => {
    const tasks = viewType === 'samples' ? sampleTasks : productionTasks;
    const idField = viewType === 'samples' ? 'sample_id' : 'production_id';
    
    return tasks.filter(task => {
      // Filter by production/sample
      if (!filteredProductionIds.includes(task[idField])) {
        return false;
      }

      // Filter by task type
      if (filters.taskType !== 'all' && task.task_type_id !== filters.taskType) {
        return false;
      }

      // Filter by supplier
      if (filters.supplierId !== 'all' && task.supplier_id !== filters.supplierId) {
        return false;
      }

      // Filter by task status
      if (filters.taskStatus !== 'all' && task.status !== filters.taskStatus) {
        return false;
      }

      return true;
    });
  }, [productionTasks, sampleTasks, filteredProductionIds, filters, viewType]);

  // Calculate KPIs
  useEffect(() => {
    console.log('Debug KPIs:', {
      viewType,
      filteredTasks: filteredTasks.length,
      filteredProductions: filteredProductions.length,
      sampleTasks: sampleTasks.length,
      productionTasks: productionTasks.length
    });
    
    // Calculate total cost from filtered tasks (both productions and samples)
    const totalCost = filteredTasks.reduce((sum, task) => {
      const cost = parseFloat(task.cost) || 0;
      return sum + (isNaN(cost) ? 0 : cost);
    }, 0);
    
    // Calculate total production value from filtered productions/samples
    const totalValue = filteredProductions.reduce((sum, item) => {
      const value = viewType === 'samples' 
        ? parseFloat(item.sample_value) || 0
        : parseFloat(item.production_value) || 0;
      return sum + (isNaN(value) ? 0 : value);
    }, 0);
    
    // Calculate average profitability
    const averageProfitability = totalValue > 0 
      ? Math.round(((totalValue - totalCost) / totalValue) * 100 * 100) / 100
      : 0;
    
    // Count tasks by status (both productions and samples)
    const pendingTasksCount = filteredTasks.filter(task => task.status === 'pending').length;
    const inProgressTasksCount = filteredTasks.filter(task => task.status === 'in_progress').length;
    const finishedTasksCount = filteredTasks.filter(task => task.status === 'finished').length;
    
    setKpis({
      averageProfitability,
      totalCost,
      totalValue,
      pendingTasksCount,
      inProgressTasksCount,
      finishedTasksCount,
    });
  }, [filteredTasks, filteredProductions, viewType]);

  // Prepare chart data for task types
  useEffect(() => {
    // Group tasks by type and calculate total cost
    const taskTypeData: Record<string, number> = {};
    
    filteredTasks.forEach(task => {
      if (task.task_type_id) {
        if (!taskTypeData[task.task_type_id]) {
          taskTypeData[task.task_type_id] = 0;
        }
        taskTypeData[task.task_type_id] += task.cost;
      }
    });
    
    // Get task type names
    const labels = Object.keys(taskTypeData).map(typeId => {
      const taskType = taskTypes.find(t => t.id === typeId);
      return taskType?.name || 'Desconocido';
    });
    
    // Define colors for each bar
    const barColors = [
      'rgba(75, 192, 192, 0.6)', // Teal
      'rgba(153, 102, 255, 0.6)', // Purple
      'rgba(255, 159, 64, 0.6)', // Orange
      'rgba(255, 99, 132, 0.6)', // Pink
      'rgba(54, 162, 235, 0.6)', // Blue
      'rgba(255, 206, 86, 0.6)', // Yellow
      'rgba(231, 233, 237, 0.6)', // Grey
      'rgba(75, 192, 192, 0.6)', // Teal (repeat)
      'rgba(153, 102, 255, 0.6)', // Purple (repeat)
      'rgba(255, 159, 64, 0.6)', // Orange (repeat)
    ];
    
    // Prepare chart data
    setTaskTypeChartData({
      labels,
      datasets: [
        {
          label: 'Costo por Tipo de Tarea',
          data: Object.values(taskTypeData),
          backgroundColor: Object.keys(taskTypeData).map((_, i) => barColors[i % barColors.length]),
          borderColor: Object.keys(taskTypeData).map((_, i) => barColors[i % barColors.length].replace('0.6', '1')),
          borderWidth: 1,
        },
      ],
    });
  }, [filteredTasks, taskTypes, isDarkMode]);

  // Prepare chart data for suppliers
  useEffect(() => {
    // Group tasks by supplier and calculate total cost
    const supplierData: Record<string, number> = {};
    
    filteredTasks.forEach(task => {
      if (task.supplier_id) {
        if (!supplierData[task.supplier_id]) {
          supplierData[task.supplier_id] = 0;
        }
        supplierData[task.supplier_id] += task.cost;
      }
    });
    
    // Get supplier names
    const labels = Object.keys(supplierData).map(supplierId => {
      const supplier = suppliers.find(s => s.id === supplierId);
      return supplier?.name || 'Desconocido';
    });
    
    // Define vibrant colors for the pie chart
    const backgroundColors = [
      '#FF6384', // Pink
      '#36A2EB', // Blue
      '#FFCE56', // Yellow
      '#4BC0C0', // Teal
      '#9966FF', // Purple
      '#FF9F40', // Orange
      '#8AC926', // Green
      '#F94144', // Red
      '#43AA8B', // Seafoam
      '#277DA1', // Blue-green
      '#F8961E', // Amber
      '#577590', // Steel blue
      '#90BE6D', // Olive
      '#F9C74F', // Gold
      '#F3722C', // Burnt orange
      '#C9184A', // Crimson
      '#4D908E', // Teal green
      '#F15BB5', // Hot pink
      '#9B5DE5', // Lavender
      '#00BBF9', // Sky blue
    ];
    
    // If we need more colors than we have defined, generate them
    if (labels.length > backgroundColors.length) {
      for (let i = backgroundColors.length; i < labels.length; i++) {
        const hue = (i * 137.5) % 360; // Golden ratio to spread colors evenly
        backgroundColors.push(`hsl(${hue}, 70%, 60%)`);
      }
    }
    
    // Create border colors (slightly darker versions of background colors)
    const borderColors = backgroundColors.map(color => {
      if (color.startsWith('#')) {
        // For hex colors, just return the same color
        return color;
      } else if (color.startsWith('hsl')) {
        // For HSL colors, reduce lightness for border
        const match = color.match(/hsl\((\d+),\s*(\d+)%,\s*(\d+)%\)/);
        if (match) {
          const [_, h, s, l] = match;
          return `hsl(${h}, ${s}%, ${Math.max(parseInt(l) - 10, 0)}%)`;
        }
      }
      return color;
    });
    
    // Prepare chart data for pie chart
    setSupplierChartData({
      labels,
      datasets: [
        {
          label: 'Costo por Proveedor',
          data: Object.values(supplierData),
          backgroundColor: backgroundColors.slice(0, labels.length),
          borderColor: borderColors.slice(0, labels.length),
          borderWidth: 1,
        },
      ],
    });
  }, [filteredTasks, suppliers, isDarkMode]);

  // Bar chart options
  const barChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          color: isDarkMode ? '#e0e0e0' : '#333',
        },
      },
      tooltip: {
        backgroundColor: isDarkMode ? '#374151' : '#fff',
        titleColor: isDarkMode ? '#e0e0e0' : '#333',
        bodyColor: isDarkMode ? '#e0e0e0' : '#333',
        borderColor: isDarkMode ? '#4b5563' : '#e2e8f0',
        borderWidth: 1,
        callbacks: {
          label: function(context: any) {
            return `${context.dataset.label}: ${formatCurrency(context.raw)}`;
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: (value: number) => formatCurrency(value),
          color: isDarkMode ? '#e0e0e0' : '#333',
        },
        grid: {
          display: false
        }
      },
      x: {
        ticks: {
          color: isDarkMode ? '#e0e0e0' : '#333',
        },
        grid: {
          display: false
        }
      }
    },
  };

  // Pie chart options
  const pieChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right' as const,
        display: true,
        labels: {
          color: isDarkMode ? '#e0e0e0' : '#333',
          font: {
            size: 12
          },
          padding: 20,
          boxWidth: 15,
          boxHeight: 15,
          generateLabels: function(chart: any) {
            const data = chart.data;
            if (data.labels.length && data.datasets.length) {
              return data.labels.map((label: string, i: number) => {
                const dataset = data.datasets[0];
                const value = dataset.data[i];
                const total = dataset.data.reduce((acc: number, val: number) => acc + val, 0);
                const percentage = Math.round((value / total) * 100);
                
                return {
                  text: `${label} - ${formatCurrency(value)} (${percentage}%)`,
                  fillStyle: dataset.backgroundColor[i],
                  strokeStyle: dataset.borderColor[i],
                  lineWidth: 1,
                  hidden: false,
                  index: i
                };
              });
            }
            return [];
          }
        },
      },
      tooltip: {
        backgroundColor: isDarkMode ? '#374151' : '#fff',
        titleColor: isDarkMode ? '#e0e0e0' : '#333',
        bodyColor: isDarkMode ? '#e0e0e0' : '#333',
        borderColor: isDarkMode ? '#4b5563' : '#e2e8f0',
        borderWidth: 1,
        callbacks: {
          label: function(context: any) {
            const label = context.label || '';
            const value = formatCurrency(context.raw);
            const percentage = Math.round(context.parsed * 100 / context.dataset.data.reduce((a: number, b: number) => a + b, 0)) + '%';
            return `${label}: ${value} (${percentage})`;
          }
        }
      }
    },
  };

  // Reset filters
  const handleResetFilters = () => {
    setFilters({
      clientId: 'all',
      productionId: 'all',
      taskType: 'all',
      supplierId: 'all',
      taskStatus: 'all',
      dateRange: 'created_at',
      startDate: '',
      endDate: '',
      period: 'all',
      customDateRange: false,
    });
    setSearchQuery('');
  };

  const handleViewDetails = (item: any) => {
    setSelectedItem(item);
    setIsDetailModalOpen(true);
  };

  // Toggle custom date range
  const handleToggleCustomDateRange = (value: boolean) => {
    setFilters(prev => ({
      ...prev,
      customDateRange: value,
      period: value ? 'custom' : prev.period
    }));
  };

  // Exportar a PDF
  const handleExportPDF = async () => {
    try {
      setIsLoading(true);
      await generateProductionAdminPDF(
        kpis,
        filteredProductions,
        filteredTasks,
        taskTypeChartData,
        supplierChartData,
        clients,
        suppliers,
        taskTypes,
        filters
      );
    } catch (error) {
      console.error('Error al generar PDF:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Administración de Producción</h2>
        <div className="flex items-center gap-2">
          {/* View Type Toggle */}
          <div className="flex bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-1">
            <button
              onClick={() => setViewType('productions')}
              className={`flex items-center px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                viewType === 'productions'
                  ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900'
                  : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              <Package className="h-4 w-4 mr-2" />
              Producciones
            </button>
            <button
              onClick={() => setViewType('samples')}
              className={`flex items-center px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                viewType === 'samples'
                  ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900'
                  : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              <Scissors className="h-4 w-4 mr-2" />
              Muestras
            </button>
          </div>
          
          <Button
            variant="outline"
            icon={<Filter className="h-4 w-4" />}
            onClick={() => setShowFilters(!showFilters)}
            size="sm"
          >
            Filtros
          </Button>
          
          <Button
            variant="outline"
            icon={<FileText className="h-4 w-4" />}
            onClick={handleExportPDF}
            disabled={isLoading}
            size="sm"
          >
            {isLoading ? 'Generando PDF...' : 'Exportar PDF'}
          </Button>
          
          <Button
            variant="outline"
            icon={<X className="h-4 w-4" />}
            onClick={handleResetFilters}
            size="sm"
          >
            Limpiar Filtros
          </Button>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <Card>
          <CardHeader title="Filtros y Búsqueda" />
          <CardContent>
            {/* Search Bar */}
            <div className="mb-4">
              <div className="relative">
                <Input
                  label="Buscar"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={`Buscar ${viewType === 'productions' ? 'producciones' : 'muestras'}...`}
                />
                <div className="absolute right-3 top-9">
                  <Search className="h-4 w-4 text-gray-400" />
                </div>
              </div>
            </div>
            
            {/* Filtros compactos */}
            <div className="flex flex-wrap md:flex-nowrap items-end gap-2 md:gap-3 lg:gap-4">
            <div className="w-full md:w-32 lg:w-40">
              <Select
                label="Cliente"
                value={filters.clientId}
                onChange={(value) => setFilters(prev => ({ ...prev, clientId: value }))}
                options={[
                  { value: 'all', label: 'Todos' },
                  ...clients.map(client => ({ value: client.id, label: client.name })),
                ]}
              />
            </div>
            <div className="w-full md:w-32 lg:w-40">
              <Select
                label="Producción"
                value={filters.productionId}
                onChange={(value) => setFilters(prev => ({ ...prev, productionId: value }))}
                options={[
                  { value: 'all', label: 'Todas' },
                  ...clientProductions.map(production => ({ value: production.id, label: production.name })),
                ]}
              />
            </div>
            <div className="w-full md:w-32 lg:w-40">
              <Select
                label="Tipo"
                value={filters.taskType}
                onChange={(value) => setFilters(prev => ({ ...prev, taskType: value }))}
                options={[
                  { value: 'all', label: 'Todos' },
                  ...taskTypes.map(type => ({ value: type.id, label: type.name })),
                ]}
              />
            </div>
            <div className="w-full md:w-32 lg:w-40">
              <Select
                label="Proveedor"
                value={filters.supplierId}
                onChange={(value) => setFilters(prev => ({ ...prev, supplierId: value }))}
                options={[
                  { value: 'all', label: 'Todos' },
                  ...suppliers.map(supplier => ({ value: supplier.id, label: supplier.name })),
                ]}
              />
            </div>
            <div className="w-full md:w-32 lg:w-40">
              <Select
                label="Estado"
                value={filters.taskStatus}
                onChange={(value) => setFilters(prev => ({ ...prev, taskStatus: value }))}
                options={[
                  { value: 'all', label: 'Todos' },
                  { value: 'pending', label: 'Pendiente' },
                  { value: 'in_progress', label: 'En Proceso' },
                  { value: 'finished', label: 'Terminado' },
                ]}
              />
            </div>
            <div className="w-full md:w-32 lg:w-40">
              <Select
                label="Período"
                value={filters.period}
                onChange={(value) => {
                  const isCustom = value === 'custom';
                  setFilters(prev => ({ 
                    ...prev, 
                    period: value,
                    customDateRange: isCustom
                  }));
                }}
                options={[
                  { value: 'all', label: 'Todo' },
                  { value: '7days', label: '7 días' },
                  { value: '30days', label: '30 días' },
                  { value: 'month', label: 'Este mes' },
                  { value: 'lastMonth', label: 'Mes anterior' },
                  { value: 'quarter', label: 'Trimestre' },
                  { value: 'year', label: 'Este año' },
                  { value: 'custom', label: 'Personalizado' },
                ]}
              />
            </div>
          </div>
          {/* Custom date range inputs */}
          {filters.customDateRange && (
            <div className="grid grid-cols-2 gap-4 mt-4">
              <Input
                label="Fecha desde"
                type="date"
                id="startDate"
                name="startDate"
                value={filters.startDate}
                onChange={(e) => setFilters(prev => ({ ...prev, startDate: e.target.value }))}
              />
              <Input
                label="Fecha hasta"
                type="date"
                id="endDate"
                name="endDate"
                value={filters.endDate}
                onChange={(e) => setFilters(prev => ({ ...prev, endDate: e.target.value }))}
              />
            </div>
          )}
        </CardContent>
      </Card>
      )}

      {/* KPIs - Financial */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="px-3 py-2">
            <div className="relative">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Rentabilidad Promedio</p>
              <p className="text-xl font-bold text-gray-900 dark:text-white mb-1">
                {kpis.averageProfitability.toFixed(2)}%
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Margen de ganancia
              </p>
              <div className="absolute top-2 right-2 w-7 h-7 bg-indigo-100 dark:bg-indigo-900/20 rounded-lg flex items-center justify-center">
                <TrendingUp className="h-3.5 w-3.5 text-indigo-600 dark:text-indigo-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="px-3 py-2">
            <div className="relative">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Costo Total</p>
              <p className="text-xl font-bold text-gray-900 dark:text-white mb-1">
                {formatCurrency(kpis.totalCost)}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Costos acumulados
              </p>
              <div className="absolute top-2 right-2 w-7 h-7 bg-red-100 dark:bg-red-900/20 rounded-lg flex items-center justify-center">
                <DollarSign className="h-3.5 w-3.5 text-red-600 dark:text-red-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="px-3 py-2">
            <div className="relative">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Valor Total</p>
              <p className="text-xl font-bold text-gray-900 dark:text-white mb-1">
                {formatCurrency(kpis.totalValue)}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Valor de producción
              </p>
              <div className="absolute top-2 right-2 w-7 h-7 bg-green-100 dark:bg-green-900/20 rounded-lg flex items-center justify-center">
                <DollarSign className="h-3.5 w-3.5 text-green-600 dark:text-green-400" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* KPIs - Task Status */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="px-3 py-2">
            <div className="relative">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Tareas Pendientes</p>
              <p className="text-xl font-bold text-gray-900 dark:text-white mb-1">
                {kpis.pendingTasksCount}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Requieren atención
              </p>
              <div className="absolute top-2 right-2 w-7 h-7 bg-amber-100 dark:bg-amber-900/20 rounded-lg flex items-center justify-center">
                <AlertTriangle className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="px-3 py-2">
            <div className="relative">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Tareas En Proceso</p>
              <p className="text-xl font-bold text-gray-900 dark:text-white mb-1">
                {kpis.inProgressTasksCount}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                En ejecución
              </p>
              <div className="absolute top-2 right-2 w-7 h-7 bg-blue-100 dark:bg-blue-900/20 rounded-lg flex items-center justify-center">
                <Clock className="h-3.5 w-3.5 text-blue-600 dark:text-blue-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="px-3 py-2">
            <div className="relative">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Tareas Terminadas</p>
              <p className="text-xl font-bold text-gray-900 dark:text-white mb-1">
                {kpis.finishedTasksCount}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Completadas
              </p>
              <div className="absolute top-2 right-2 w-7 h-7 bg-green-100 dark:bg-green-900/20 rounded-lg flex items-center justify-center">
                <CheckCircle className="h-3.5 w-3.5 text-green-600 dark:text-green-400" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader title="Costo por Tipo de Tarea" />
          <CardContent>
            <div className="h-80">
              {taskTypeChartData && taskTypeChartData.labels.length > 0 ? (
                <Bar data={taskTypeChartData} options={barChartOptions} />
              ) : (
                <div className="h-full flex items-center justify-center text-gray-500 dark:text-gray-400">
                  No hay datos disponibles para mostrar
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader title="Costo por Proveedor" />
          <CardContent>
            <div className="h-80">
              {supplierChartData && supplierChartData.labels.length > 0 ? (
                <Pie data={supplierChartData} options={pieChartOptions} />
              ) : (
                <div className="h-full flex items-center justify-center text-gray-500 dark:text-gray-400">
                  No hay datos disponibles para mostrar
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Summary */}
      <Card>
        <CardHeader title={`Resumen de ${viewType === 'productions' ? 'Producciones' : 'Muestras'}`} />
        <CardContent>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {viewType === 'productions' ? 'Producción' : 'Muestra'}
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Cliente
                  </th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Valor
                  </th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Costo
                  </th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Rentabilidad
                  </th>
                  <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Tareas
                  </th>
                  <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                {filteredProductions.map((item) => {
                  const idField = viewType === 'productions' ? 'production_id' : 'sample_id';
                  const itemTasks = filteredTasks.filter(task => task[idField] === item.id);
                  const totalCost = itemTasks.reduce((sum, task) => {
                    const cost = parseFloat(task.cost) || 0;
                    return sum + (isNaN(cost) ? 0 : cost);
                  }, 0);
                  const itemValue = viewType === 'samples' 
                    ? parseFloat(item.sample_value) || 0
                    : parseFloat(item.production_value) || 0;
                  const profitability = itemValue > 0 
                    ? Math.round(((itemValue - totalCost) / itemValue) * 100 * 100) / 100
                    : 0;
                  
                  const client = clients.find(c => c.id === item.client_id);
                  
                  return (
                    <tr 
                      key={item.id} 
                      className="hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer transition-colors"
                      onClick={() => handleViewDetails(item)}
                    >
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                        <div className="flex items-center">
                          {viewType === 'productions' ? (
                            <Package className="h-4 w-4 mr-2 text-indigo-600 dark:text-indigo-400" />
                          ) : (
                            <Scissors className="h-4 w-4 mr-2 text-purple-600 dark:text-purple-400" />
                          )}
                          {item.name}
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {client?.name || 'Cliente no encontrado'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-900 dark:text-white">
                        {formatCurrency(itemValue)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-900 dark:text-white">
                        {formatCurrency(totalCost)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-right">
                        <span className={`
                          ${profitability > 30 ? 'text-green-600 dark:text-green-400' : 
                            profitability > 15 ? 'text-blue-600 dark:text-blue-400' : 
                            'text-red-600 dark:text-red-400'}
                        `}>
                          {profitability.toFixed(2)}%
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-center">
                        <div className="flex justify-center space-x-1">
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200">
                            {itemTasks.filter(t => t.status === 'pending').length}
                          </span>
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200">
                            {itemTasks.filter(t => t.status === 'in_progress').length}
                          </span>
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200">
                            {itemTasks.filter(t => t.status === 'finished').length}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-center">
                        <Button
                          variant="outline"
                          size="sm"
                          icon={<Eye className="h-3 w-3" />}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewDetails(item);
                          }}
                        >
                          Ver
                        </Button>
                      </td>
                    </tr>
                  );
                })}
                {filteredProductions.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-4 text-center text-sm text-gray-500 dark:text-gray-400">
                      No hay {viewType === 'productions' ? 'producciones' : 'muestras'} que coincidan con los filtros
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Detail Modal */}
      {isDetailModalOpen && selectedItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Detalles de {viewType === 'productions' ? 'Producción' : 'Muestra'}
              </h3>
              <Button
                variant="outline"
                size="sm"
                icon={<X className="h-4 w-4" />}
                onClick={() => setIsDetailModalOpen(false)}
              >
                Cerrar
              </Button>
            </div>
            
            <div className="space-y-4">
              <div>
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Información General</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Nombre</p>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{selectedItem.name}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Cliente</p>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {clients.find(c => c.id === selectedItem.client_id)?.name || 'Cliente no encontrado'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Valor</p>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {formatCurrency(
                        viewType === 'samples' 
                          ? selectedItem.sample_value || 0
                          : selectedItem.production_value || 0
                      )}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Fecha de Creación</p>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {new Date(selectedItem.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Tareas Asociadas</h4>
                <div className="space-y-2">
                  {(() => {
                    const idField = viewType === 'productions' ? 'production_id' : 'sample_id';
                    return filteredTasks.filter(task => task[idField] === selectedItem.id).map(task => (
                      <div key={task.id} className="flex justify-between items-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                        <div>
                          <p className="text-sm font-medium text-gray-900 dark:text-white">{task.description}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {taskTypes.find(tt => tt.id === task.task_type_id)?.name || 'Tipo no encontrado'}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-medium text-gray-900 dark:text-white">
                            {formatCurrency(task.cost)}
                          </p>
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs ${
                            task.status === 'pending' ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200' :
                            task.status === 'in_progress' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200' :
                            'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200'
                          }`}>
                            {task.status === 'pending' ? 'Pendiente' :
                             task.status === 'in_progress' ? 'En Proceso' : 'Terminada'}
                          </span>
                        </div>
                      </div>
                    ));
                  })()}
                  {(() => {
                    const idField = viewType === 'productions' ? 'production_id' : 'sample_id';
                    const itemTasks = filteredTasks.filter(task => task[idField] === selectedItem.id);
                    if (itemTasks.length === 0) {
                      return (
                        <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
                          No hay tareas asociadas
                        </p>
                      );
                    }
                    return null;
                  })()}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductionAdmin;